import { Autocomplete } from "components/Form/components/Autocomplete/Autocomplete";
import { Button } from "components/Button/Button";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { useCallback, useMemo } from "react";
import {
    AssigneeBadgeVariant,
    AssigneeOption,
    buildHeaderAssigneeOptions,
    resolveAssigneeSelection,
} from "./commentAssignee";
import styles from "./CommentsPanel.module.scss";

interface CommentAssigneeControlProps {
    commentId: string;
    assigneeId: string | null;
    assigneeLabel: string;
    resolved: boolean;
    variant: AssigneeBadgeVariant;
    userOptions: AssigneeOption[];
    // The viewer may change the assignee (author of the comment or write access
    // to the record). Otherwise the chip is inert display text.
    canAssign: boolean;
    saving: boolean;
    onAssign: (assigneeId: string | null) => void;
}

/**
 * The assignee control in a posted comment's header
 * (operator-task-fe-comment-panel-space-saving-collapse-composer-and-compact-assign,
 * Margot 2026-11-03):
 * - unassigned: a small "Assign" button;
 * - assigned: the assignee chip itself is the control (chevron = it opens a menu);
 * clicking either opens the standard searchable user picker directly underneath
 * it. Picking a user saves at once; "Nobody" removes the assignment.
 * Without assign rights the chip is inert display text
 * (ui-design-principle-affordance-interactivity).
 */
export const CommentAssigneeControl = ({
    commentId,
    assigneeId,
    assigneeLabel,
    resolved,
    variant,
    userOptions,
    canAssign,
    saving,
    onAssign,
}: CommentAssigneeControlProps) => {
    const chipClassName = `${styles.assigneeBadge}${
        { mine: ` ${styles.assigneeBadgeMine}`, other: "", done: ` ${styles.assigneeBadgeDone}` }[
            variant
        ]
    }`;
    const chipText = `${resolved ? "Done · " : ""}Assigned to ${assigneeLabel}`;
    const chipTitle = `Assigned to ${assigneeLabel}${resolved ? " — done" : ""}`;

    const options = useMemo(
        () => buildHeaderAssigneeOptions(userOptions, !!assigneeId),
        [userOptions, assigneeId]
    );

    const handleChange = useCallback(
        (selected: string | null) => {
            const next = resolveAssigneeSelection(selected, assigneeId);
            if (next !== undefined) {
                onAssign(next);
            }
        },
        [assigneeId, onAssign]
    );

    const trigger = useCallback(
        () =>
            assigneeId ? (
                <button
                    type="button"
                    className={`${chipClassName} ${styles.assigneeBadgeButton}`}
                    title={`${chipTitle} — click to change`}
                    disabled={saving}
                >
                    {chipText}
                    <Icon icon={IconCollection.chevronDown} />
                </button>
            ) : (
                <Button
                    label="Assign"
                    title="Assign this comment to a person"
                    icon={IconCollection.accountOutline}
                    variation="white"
                    size="small"
                    loading={saving}
                />
            ),
        [assigneeId, chipClassName, chipText, chipTitle, saving]
    );

    if (!canAssign) {
        return assigneeId ? (
            <span className={chipClassName} title={chipTitle}>
                {chipText}
            </span>
        ) : null;
    }

    return (
        <span className={styles.assigneeControl}>
            <Autocomplete
                name={`comment-assignee-${commentId}`}
                options={{ list: options }}
                value={assigneeId}
                onChange={handleChange}
                isDisabled={saving}
                additionalProps={{
                    renderIconOnly: true,
                    iconOnlyComponent: trigger,
                    customKey: `comment-assignee-${commentId}`,
                }}
            />
        </span>
    );
};
